import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

export type FileDropStatus = 'idle' | 'queued' | 'uploading' | 'compressing' | 'processing' | 'completed' | 'failed';

@Component({
  selector: 'ss-file-drop',
  templateUrl: './file-drop.component.html',
  standalone: false
})
export class FileDropComponent {
  private static nextId = 0;
  readonly inputId = `ss-file-${++FileDropComponent.nextId}`;

  @Input() accept = '';
  @Input() title = 'Arraste e solte o arquivo ou clique para selecionar';
  @Input() subtitle = '';
  @Input() disabled = false;
  @Input() maxSize = 0;
  @Input() file: File | null = null;
  @Input() previewUrl = '';
  @Input() progress = 0;
  @Input() status: FileDropStatus = 'idle';
  @Input() error = '';
  @Output() fileSelected = new EventEmitter<File>();
  @Output() fileChange = new EventEmitter<File | null>();
  @Output() rejected = new EventEmitter<string>();

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  dragging = false;
  validationError = '';

  get progressValue(): number {
    return Math.max(0, Math.min(100, this.progress));
  }

  get message(): string {
    return this.error || this.validationError;
  }

  get fileSize(): string {
    const size = this.file?.size || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
    return `${(size / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
  }

  drag(event: DragEvent, active: boolean) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled) this.dragging = active;
  }

  drop(event: DragEvent) {
    this.drag(event, false);
    const file = event.dataTransfer?.files[0];
    if (!this.disabled && file) this.select(file);
  }

  choose(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) this.select(input.files[0]);
    input.value = '';
  }

  select(file: File) {
    if (this.disabled) return;

    const message = !this.isAccepted(file)
      ? 'Formato de arquivo não permitido.'
      : this.maxSize && file.size > this.maxSize ? 'O arquivo excede o tamanho permitido.' : '';

    if (message) {
      this.validationError = message;
      this.rejected.emit(message);
      return;
    }

    this.validationError = '';
    this.file = file;
    this.fileSelected.emit(file);
    this.fileChange.emit(file);
  }

  clear(emit = false) {
    this.file = null;
    this.validationError = '';
    this.dragging = false;
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    if (emit) this.fileChange.emit(null);
  }

  private isAccepted(file: File): boolean {
    const rules = this.accept.split(',').map((rule) => rule.trim().toLowerCase()).filter(Boolean);
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();

    return !rules.length || rules.some((rule) => {
      if (rule.startsWith('.')) return name.endsWith(rule);
      if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
      return type === rule;
    });
  }
}
